import * as React from 'react';
import type { GameSummary, Phase } from '@chess/api-model';
import { Skeleton } from '@chess/api-skeleton';
import { signed } from '@chess/api-insights/charts';
import { detail, gameTitle, outcome, timeClassOf, type GameMetrics } from './text';

// The selected game on Home, drawn from props only: its title under the game
// strip (`GameTitleBar`: the result, "EdBlackKnight 1700 vs rival 1722",
// the pace and the date; 2026-10-10, the card's own header before), and the card
// (after v1's LatestGameCard): a diagram of the position the game ended on,
// how it ended, the engine's verdict (the accuracy against the reader's
// usual, each stage's score and accuracy with the deciding one marked,
// blunders and misses), and the way into its review; since 2026-10-10 in four columns
// (diagram, accuracy, stage tiles, errors) over a footer. The verdict card beside it went on
// 2026-10-10: its sentences repeated the card, and its two facts of its own
// (the usual accuracy, the deciding stage) are the card's now. The
// container (index.tsx) reads the store.

const GLYPH: Record<string, string> = {
  K: '♔', Q: '♕', R: '♖', B: '♗', N: '♘', P: '♙',
  k: '♚', q: '♛', r: '♜', b: '♝', n: '♞', p: '♟',
};

/** Expands the piece placement of a FEN into 64 squares, a8 first. */
function squares(placement: string): Array<string | undefined> {
  const out: Array<string | undefined> = [];
  for (const ch of placement.replace(/\//g, '')) {
    const empty = Number(ch);
    if (Number.isInteger(empty)) {
      for (let i = 0; i < empty; i++) out.push(undefined);
    } else {
      out.push(ch);
    }
  }
  return out;
}

const Diagram: React.FC<{ fen: string; label: string }> = ({ fen, label }) => (
  <div className="latest-game-diagram" role="img" aria-label={label}>
    {squares(fen.split(' ')[0]).map((piece, i) => {
      const dark = (Math.floor(i / 8) + i) % 2 === 1;
      return (
        <span key={i} className={`latest-game-square${dark ? ' dark' : ''}${piece ? (piece === piece.toUpperCase() ? ' white' : ' black') : ''}`}>
          {piece ? GLYPH[piece] : ''}
        </span>
      );
    })}
  </div>
);

export interface LatestGameCardData {
  game: GameSummary;
  /** The position the game ended on. */
  finalFen: string;
  plies: number;
  headers: Record<string, string>;
  /** Each stage's score and the reader's blunders and misses; undefined until the game is scanned. */
  metrics?: GameMetrics;
  /** The review's address, for the link. */
  href: string;
  /** Opens the review; the whole card and its button call it. */
  onOpen(): void;
  /** The review's address at a stage's start (`#ply=`), for a reached stage's tile; undefined when the stage has no start. */
  stageHref?(phase: Phase): string | undefined;
  /** Opens the review at a stage's start; a reached stage's tile calls it. */
  onOpenStage?(phase: Phase): void;
  /** The reader's average accuracy over the period, set beside the game's. */
  usualAccuracy?: number;
  /** The stage that decided the game (the verdict's rule), its score marked. */
  decided?: Phase;
}

/** The card's data, or `loading` while the store still reads the latest game: then a skeleton stands in for each part. */
export type LatestGameCardProps = ({ loading?: false } & LatestGameCardData) | { loading: true };

const text = (sample: string) => <Skeleton loading inline variant="text" shape={sample} />;

const STAGES = [
  ['opening', 'Opening'],
  ['middlegame', 'Middlegame'],
  ['ending', 'Ending'],
] as const;

/** Positive, negative or level: the score's colour. */
const tone = (pawns: number) => (pawns >= 0.05 ? ' good' : pawns <= -0.05 ? ' bad' : '');

/** A stage bar's half-width, in pawns: a score this large or larger fills its half. */
const STAGE_SPAN = 5;

const percent = (value: number) => `${Math.min(100, Math.max(0, value))}%`;

/**
 * The reader's accuracy, the game's one overall measure (2026-10-08), big;
 * "—" when the scan gave none. Under it a bar 0–100 filled to it, with a
 * tick at the reader's usual accuracy and "your usual N" (both only when
 * the usual is known).
 */
const Accuracy: React.FC<{ value?: number; usual?: number; loading?: boolean }> = ({ value, usual, loading = false }) => (
  <div className="latest-game-accuracy">
    <span className="latest-game-accuracy-value">{loading ? text('80') : value === undefined ? '—' : Math.round(value)}</span>
    <span className="latest-game-accuracy-label">Accuracy</span>
    <span className="latest-game-accuracy-bar" aria-hidden="true">
      {!loading && value !== undefined && <span className="latest-game-accuracy-fill" style={{ width: percent(value) }} />}
      {!loading && usual !== undefined && <span className="latest-game-accuracy-tick" style={{ left: percent(usual) }} />}
    </span>
    {loading ? (
      <span className="latest-game-accuracy-usual">{text('your usual 80')}</span>
    ) : usual !== undefined ? (
      <span className="latest-game-accuracy-usual" title="Your average accuracy over the last 30 days' scanned games">
        your usual {Math.round(usual)}
      </span>
    ) : null}
  </div>
);

/**
 * One stage as a tile, a column spread over the tile's full height
 * (2026-10-10): its name at the top ("decided it" at the right on the
 * deciding stage); in the middle two figures side by side, the accuracy
 * first (2026-10-10: accuracy before the score change wherever both show),
 * the reader's accuracy over the stage over "accuracy" ("—" when the stage
 * had too few of the reader's moves to score), then at the right its score
 * (the score after the opening, the change across the middlegame and the
 * ending, the reader's side, as the Score by stage card) over "score", both
 * in the value role; at the bottom a bar diverging from
 * a zero tick (a gain to the right, a loss to the left, `STAGE_SPAN` pawns to
 * each side). The deciding stage is outlined; a stage the game did not reach
 * reads "—" twice and "not reached" in place of the bar.
 *
 * A reached stage's tile is a link (2026-10-10) to the review at the
 * stage's start (`href`): a click calls `onOpen` and stops there, so the
 * card's own click does not fire; a modified click (a new tab) is the
 * browser's. The tiles are spans in a list, not a `<dl>`, since a link may
 * not wrap a term and its description.
 */
const StageTile: React.FC<{
  /** The stage, the click's `data-track-value`. */
  phase: Phase;
  label: string;
  pawns?: number;
  accuracy?: number;
  decided: boolean;
  loading: boolean;
  href?: string;
  onOpen?(): void;
}> = ({ phase, label, pawns, accuracy, decided, loading, href, onOpen }) => {
  const reached = loading || pawns !== undefined;
  const link = !loading && pawns !== undefined && href !== undefined;
  const cls = `latest-game-stage${link ? ' latest-game-stage-link' : ''}${decided ? ` latest-game-stage-decided${pawns !== undefined && pawns > 0 ? ' latest-game-stage-decided-win' : ''}` : ''}`;
  const body = (
    <>
      <span className="latest-game-stage-head">
        <span>{label}</span>
        {decided && <span className="latest-game-stage-note">decided it</span>}
      </span>
      <span className="latest-game-stage-figures">
        <span className="latest-game-stage-figure">
          <span className="latest-game-stage-accuracy">
            {loading ? text('80') : pawns === undefined || accuracy === undefined ? '—' : Math.round(accuracy)}
          </span>
          <span className="latest-game-stage-caption">accuracy</span>
        </span>
        <span className="latest-game-stage-figure">
          <span className={`latest-game-stage-score${pawns === undefined ? '' : tone(pawns)}`}>
            {loading ? text('+0.00') : pawns === undefined ? '—' : signed(pawns)}
          </span>
          <span className="latest-game-stage-caption">score</span>
        </span>
      </span>
      {reached ? (
        <span className="latest-game-stage-bar" aria-hidden="true">
          {!loading && pawns !== undefined && (
            <span
              className={`latest-game-stage-fill ${pawns >= 0 ? 'gain' : 'loss'}`}
              style={{ width: `${(Math.min(Math.abs(pawns), STAGE_SPAN) / STAGE_SPAN) * 50}%` }}
            />
          )}
        </span>
      ) : (
        <span className="latest-game-stage-missing">not reached</span>
      )}
    </>
  );
  if (!link) {
    return (
      <div className={cls} role="listitem">
        {body}
      </div>
    );
  }
  const title = `Open the review at the ${label.toLowerCase()}`;
  return (
    <div className="latest-game-stage-item" role="listitem">
      <a
        className={cls}
        href={href}
        title={title}
        aria-label={title}
        data-track="home.card.stage"
        data-track-value={phase}
        onClick={(e) => {
          e.stopPropagation();
          if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          e.preventDefault();
          onOpen?.();
        }}
        onKeyDown={(e) => {
          // Enter on a link clicks it; the card's own keys (Enter, Space) stay out of it.
          if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
        }}>
        {body}
      </a>
    </div>
  );
};

/** The reader's blunders and misses, each count big over its noun. */
const Errors: React.FC<{ metrics?: GameMetrics; loading?: boolean }> = ({ metrics, loading = false }) => {
  const count = (n: number | undefined, cls: string, one: string, many: string) => (
    <div className="latest-game-error">
      <span className={`latest-game-error-count${n ? ` ${cls}` : ''}`}>{loading || n === undefined ? text('0') : n}</span>
      <span className="latest-game-error-label">{n === 1 ? one : many}</span>
    </div>
  );
  return (
    <div className="latest-game-errors">
      {count(metrics?.blunders, 'blunders', 'blunder', 'blunders')}
      {count(metrics?.misses, 'misses', 'miss', 'misses')}
    </div>
  );
};

/**
 * The engine's verdict on the game, columns 2–4 of the card: the accuracy,
 * the three stages as tiles, the errors. Before the scan completes, a line
 * saying so in their place; while loading, skeletons in their places.
 */
const Metrics: React.FC<{
  metrics?: GameMetrics;
  usual?: number;
  decided?: Phase;
  loading?: boolean;
  stageHref?(phase: Phase): string | undefined;
  onOpenStage?(phase: Phase): void;
}> = ({ metrics, usual, decided, loading = false, stageHref, onOpenStage }) => {
  if (!loading && !metrics) return <p className="latest-game-metrics-wait">Stage scores and errors appear once the game is scanned.</p>;
  return (
    <>
      <Accuracy value={metrics?.accuracy} usual={usual} loading={loading} />
      <div className="latest-game-stages" role="list" aria-label="Score per stage">
        {STAGES.map(([phase, label]) => (
          <StageTile
            key={phase}
            phase={phase}
            label={label}
            pawns={metrics?.stages[phase]}
            accuracy={metrics?.stageAccuracy?.[phase]}
            decided={!loading && decided === phase}
            loading={loading}
            href={stageHref?.(phase)}
            onOpen={onOpenStage ? () => onOpenStage(phase) : undefined}
          />
        ))}
      </div>
      <Errors metrics={metrics} loading={loading} />
    </>
  );
};


/** The result as a badge in its colour before the title (2026-10-08), so a win or a loss reads at a glance. */
const ResultBadge: React.FC<{ game: GameSummary }> = ({ game }) => {
  const how = outcome(game);
  if (!how) return null;
  const word = how.charAt(0).toUpperCase() + how.slice(1);
  return <span className={`latest-game-result latest-game-result-${how}`}>{word}</span>;
};

/** The card while loading: the same four columns and footer, each part a skeleton in its place. */
const LatestGameSkeleton: React.FC = () => (
  <article className="card viz home-card-latest-game" aria-busy="true">
    <div className="latest-game-grid">
      <Skeleton loading variant="rounded" width="8.25rem" height="8.25rem" label="Loading the latest game" />
      <Metrics loading />
      <div className="latest-game-foot">
        <span className="latest-game-detail">{text('38 moves · You lost by resignation · Sicilian Defense')}</span>
        <Skeleton loading inline shape={<span className="btn btn-primary">Analyze this game ›</span>} />
      </div>
    </div>
  </article>
);

export const LatestGameCard: React.FC<LatestGameCardProps> = (props) => (props.loading ? <LatestGameSkeleton /> : <LoadedCard {...props} />);

/**
 * The card (2026-10-10, four columns): the diagram, the accuracy, the three
 * stage tiles and the errors, over a footer with the detail line and the
 * button. The whole card opens the review, as does its button; a reached
 * stage's tile opens it at the stage's start.
 */
const LoadedCard: React.FC<LatestGameCardData> = ({ game, finalFen, plies, headers, metrics, href, onOpen, stageHref, onOpenStage, usualAccuracy, decided }) => (
  <article
    className="card card-clickable viz home-card-latest-game"
    role="link"
    tabIndex={0}
    aria-label="Analyze this game"
    data-track="home.card.analyze"
    onClick={onOpen}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onOpen();
      }
    }}>
    <div className="latest-game-grid">
      <Diagram fen={finalFen} label={`The final position of ${game.white.name} vs. ${game.black.name}`} />
      <Metrics metrics={metrics} usual={usualAccuracy} decided={decided} stageHref={stageHref} onOpenStage={onOpenStage} />
      <div className="latest-game-foot">
        <span className="latest-game-detail">{detail(game, plies, headers)}</span>
        <a
          className="btn btn-primary"
          href={href}
          data-track="home.card.analyze"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onOpen();
          }}>
          Analyze this game ›
        </a>
      </div>
    </div>
  </article>
);

/**
 * The game's title under Home's game strip: the result badge, then
 * "EdBlackKnight 1700 vs rival 1722" (the owner first, by their username),
 * each rating after its player, then the pace and the date. It heads
 * everything below it on Home.
 */
export const GameTitleBar: React.FC<{ loading: true; game?: undefined; headers?: undefined } | { loading?: false; game: GameSummary; headers: Record<string, string> }> = (props) => {
  if (props.loading) {
    return (
      <h2 className="game-title" aria-busy="true">
        {text('Lost username 1700 vs opponent 1700 · Blitz 5+0 · Oct 9')}
      </h2>
    );
  }
  const t = gameTitle(props.game, timeClassOf(props.headers));
  return (
    <h2 className="game-title">
      <ResultBadge game={props.game} />
      <span className="game-title-player">
        {t.me}
        {t.myRating !== undefined && <span className="game-title-rating">{t.myRating}</span>}
      </span>
      <span className="game-title-vs">vs</span>
      <span className="game-title-player">
        {t.name}
        {t.rating !== undefined && <span className="game-title-rating">{t.rating}</span>}
      </span>
      {t.pace && <span className="game-title-meta">· {t.pace}</span>}
      {t.date && <span className="game-title-meta">· {t.date}</span>}
    </h2>
  );
};
